// src/utils/stageDataRender.tsx
// Shared helpers for rendering a completed stage's stage_data read-only —
// used by StageDataModal.tsx (generic fallback for any stage without a
// dedicated summary view, and for Consultation's long AI-document body) and
// by StageSummaryView.tsx (the "additional details" catch-all for fields a
// stage's known-field renderer doesn't explicitly handle). Kept in one place
// so both never drift on what "empty" or "humanized" means.
import { Fragment, type ReactNode } from 'react';

export function humanize(key: string): string {
  return key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, c => c.toUpperCase());
}

export function isEmptyValue(v: unknown): boolean {
  return v === null || v === undefined || v === '' ||
    (Array.isArray(v) && v.length === 0) ||
    (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);
}

// AI-generated documents (consultation drafts, discharge summaries) are
// stored as one long string with embedded newlines and "**bold**"/"---"
// markdown-ish markers — rendered through a plain <span> that collapses
// whitespace, the whole thing ran together as one unreadable line. Mirrors
// ConsultationChat's renderMsg but with proper inline (not just whole-line)
// bold handling, since these documents bold just a field label mid-line
// (e.g. "**Date:** 22 July 2026").
function renderInlineBold(line: string): ReactNode[] {
  return line.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    /^\*\*[^*]+\*\*$/.test(part) ? <b key={i}>{part.slice(2, -2)}</b> : <Fragment key={i}>{part}</Fragment>
  );
}

export function renderTextBlock(text: string): ReactNode {
  return (
    <div>
      {text.split('\n').map((line, i) => {
        const trimmed = line.trim();
        if (/^-{2,}.*-{2,}$/.test(trimmed)) {
          return <div key={i} style={{ fontSize:11, color:'#92400E', background:'#FEF3C7', borderRadius:4, padding:'2px 8px', display:'inline-block', marginTop:6, marginBottom:4 }}>{trimmed.replace(/-/g, '').trim()}</div>;
        }
        if (/^Rx\.\s/.test(trimmed)) {
          return <div key={i} style={{ fontFamily:'var(--mono)', fontSize:12, padding:'2px 0', color:'#1D4ED8' }}>{line}</div>;
        }
        if (/^[-•]\s/.test(trimmed)) {
          return <div key={i} style={{ paddingLeft:14, marginBottom:2 }}>• {renderInlineBold(trimmed.replace(/^[-•]\s/, ''))}</div>;
        }
        return <div key={i} style={{ marginBottom: trimmed ? 3 : 8 }}>{trimmed ? renderInlineBold(line) : null}</div>;
      })}
    </div>
  );
}

export function RenderValue({ value }: { value: unknown }): ReactNode {
  if (Array.isArray(value)) {
    if (value.length === 0) return <span style={{ color:'#9CA3AF' }}>—</span>;
    if (value.every(v => typeof v !== 'object')) return <span>{value.join(', ')}</span>;
    return <RenderObject obj={value} />;
  }
  if (value && typeof value === 'object') return <RenderObject obj={value as Record<string, unknown>} />;
  if (typeof value === 'string' && value.includes('\n')) return renderTextBlock(value);
  if (typeof value === 'string' && value.includes('**')) return <span>{renderInlineBold(value)}</span>;
  return <span>{String(value)}</span>;
}

export function RenderObject({ obj }: { obj: Record<string, unknown> | unknown[] }): ReactNode {
  const entries: [string | number, unknown][] = Array.isArray(obj)
    ? obj.map((v, i): [number, unknown] => [i, v])
    : Object.entries(obj);
  const visible = entries.filter(([, v]) => !isEmptyValue(v));
  if (!visible.length) return <span style={{ color:'#9CA3AF' }}>—</span>;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:6, marginTop:4, paddingLeft:12, borderLeft:'2px solid #F3F4F6' }}>
      {visible.map(([k, v]) => (
        <div key={k}>
          <span style={{ fontSize:12, fontWeight:600, color:'#6B7280' }}>{Array.isArray(obj) ? `#${Number(k)+1}` : humanize(String(k))}: </span>
          <span style={{ fontSize:13, color:'#111827' }}><RenderValue value={v} /></span>
        </div>
      ))}
    </div>
  );
}
